import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from '@aws-amplify/auth';
import { useAuth } from '../../auth/useAuth.ts';

type Screen = 'LOGIN' | 'NEW_PASSWORD_REQUIRED' | 'FORGOT_REQUEST' | 'FORGOT_CONFIRM' | 'TOTP_CODE';

const inputCls =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-phoenix-blue-500 focus:outline-none focus:ring-1 focus:ring-phoenix-blue-500';
const btnCls =
  'w-full rounded-md bg-phoenix-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-phoenix-blue-700 disabled:opacity-50';

const PASSWORD_REQUIREMENTS =
  'At least 12 characters, including an uppercase letter, a lowercase letter, and a number.';

const PW_RULES = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number', test: (p: string) => /[0-9]/.test(p) },
];

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  return (
    <ul className="mt-2 space-y-1">
      {PW_RULES.map(({ label, test }) => {
        const ok = test(password);
        return (
          <li key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{ok ? '✓' : '○'}</span>
            <span>{label}</span>
          </li>
        );
      })}
    </ul>
  );
}

function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95M9.88 9.88a3 3 0 104.243 4.243M6.343 6.343A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.953 9.953 0 01-1.563 2.912M3 3l18 18" />
    </svg>
  );
}

function PasswordInput({
  value,
  onChange,
  autoComplete,
  required,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  required?: boolean;
  id?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required={required}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-phoenix-blue-500 focus:outline-none focus:ring-1 focus:ring-phoenix-blue-500"
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

export function LoginPage() {
  const { login, completeNewPassword, confirmTOTPCode, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/portal';

  const [screen, setScreen] = useState<Screen>('LOGIN');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setScreen('NEW_PASSWORD_REQUIRED');
      } else if (result.challengeName === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setTotpCode('');
        setScreen('TOTP_CODE');
      } else if (result.challengeName === 'RESET_PASSWORD') {
        // An admin reset this account — a verification code was already sent to the inbox.
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
        setError('A password reset has been initiated on your account. Check your email for a verification code, then set a new password below.');
        setScreen('FORGOT_CONFIRM');
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await completeNewPassword(newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set new password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTOTPCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await confirmTOTPCode(totpCode);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotRequest = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await resetPassword({ username: email });
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
      setScreen('FORGOT_CONFIRM');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: resetCode, newPassword });
      // Auto-login after a successful reset — no need for a second sign-in.
      await login(email, newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed. Check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-phoenix-blue-950 px-4 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-serif text-3xl font-bold text-white">Blue Phoenix Designs</h1>
          <p className="mt-2 text-sm text-phoenix-blue-200">Client Portal</p>
        </div>

        <div className="rounded-lg bg-white p-8 shadow-xl">
          {screen === 'LOGIN' && (
            <>
              <h2 className="mb-6 text-xl font-semibold text-gray-900">Sign In</h2>
              <form
                onSubmit={(e) => {
                  void handleLogin(e);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setScreen('FORGOT_REQUEST');
                      }}
                      className="text-xs text-phoenix-blue-600 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <PasswordInput value={password} onChange={setPassword} autoComplete="current-password" required />
                </div>
                {error && (
                  <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                )}
                <button type="submit" disabled={isLoading} className={btnCls}>
                  {isLoading ? 'Signing in…' : 'Sign In'}
                </button>
                <p className="text-center text-xs text-gray-400">
                  Access for clients and trades with an account.
                </p>
              </form>
            </>
          )}

          {screen === 'NEW_PASSWORD_REQUIRED' && (
            <>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">Set New Password</h2>
              <p className="mb-4 text-sm text-gray-500">
                Choose a permanent password to continue.
              </p>
              <p className="mb-4 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-400">
                Requirements: {PASSWORD_REQUIREMENTS}
              </p>
              <form
                onSubmit={(e) => {
                  void handleNewPassword(e);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <PasswordInput value={newPassword} onChange={setNewPassword} autoComplete="new-password" required />
                  <PasswordStrength password={newPassword} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <PasswordInput value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" required />
                </div>
                {error && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={isLoading} className={btnCls}>
                  {isLoading ? 'Setting password…' : 'Set Password'}
                </button>
              </form>
            </>
          )}

          {screen === 'TOTP_CODE' && (
            <>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">Verify It&apos;s You</h2>
              <p className="mb-6 text-sm text-gray-500">
                This is a new or unrecognized device. Enter the 6-digit code from your
                authenticator app to continue.
              </p>
              <form
                onSubmit={(e) => {
                  void handleTOTPCode(e);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">Authentication Code</label>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.trim())}
                    className={inputCls}
                    placeholder="123456"
                  />
                </div>
                {error && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={isLoading} className={btnCls}>
                  {isLoading ? 'Verifying…' : 'Verify'}
                </button>
              </form>
            </>
          )}

          {screen === 'FORGOT_REQUEST' && (
            <>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">Reset Password</h2>
              <p className="mb-6 text-sm text-gray-500">
                Enter your email address and we&apos;ll send you a verification code.
              </p>
              <form
                onSubmit={(e) => {
                  void handleForgotRequest(e);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                  />
                </div>
                {error && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={isLoading} className={btnCls}>
                  {isLoading ? 'Sending…' : 'Send Reset Code'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setScreen('LOGIN');
                  }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Back to sign in
                </button>
              </form>
            </>
          )}

          {screen === 'FORGOT_CONFIRM' && (
            <>
              <h2 className="mb-1 text-xl font-semibold text-gray-900">Enter Reset Code</h2>
              <p className="mb-6 text-sm text-gray-500">
                Check your email at <strong>{email}</strong> for a verification code, then choose a new password.
              </p>
              <form
                onSubmit={(e) => {
                  void handleForgotConfirm(e);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700">Verification Code</label>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.trim())}
                    className={inputCls}
                    placeholder="123456"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Password</label>
                  <PasswordInput value={newPassword} onChange={setNewPassword} autoComplete="new-password" required />
                  <PasswordStrength password={newPassword} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                  <PasswordInput value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" required />
                </div>
                {error && (
                  <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}
                <button type="submit" disabled={isLoading} className={btnCls}>
                  {isLoading ? 'Resetting…' : 'Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setScreen('FORGOT_REQUEST');
                  }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Didn&apos;t receive a code? Send again
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
